import { aiAgentServices, helpers, transcriptionServices } from 'src/services';
import { BsStars } from 'react-icons/bs';
import { generalBlue } from 'src/app-config';
import { hideLoadingModalReducer, setAlertResponse, showLoadingModalReducer } from 'src/store';
import { Hr } from './Hr';
import { LuMic, LuMicOff } from 'react-icons/lu';
import { Modal } from 'react-bootstrap';
import { useDispatch } from 'react-redux';
import { useEffect, useRef, useState } from 'react';
import { useIsAppleDevice } from 'src/custom-hooks';

export const AiFloatingWidget = () => {
    const [showingAiPrompt, setShowingAiPrompt] = useState(false);

    return (
        <>
        {showingAiPrompt &&
            <AiPrompt closeModal={() => setShowingAiPrompt(false)} />
        }
        <button
            onClick={() => setShowingAiPrompt(true)}
            style={{
                position: 'fixed',
                bottom: '24px',
                right: '24px',
                width: '56px',
                height: '56px',
                borderRadius: '50%',
                background: generalBlue,
                border: 'none',
                boxShadow: '0 6px 18px rgba(0,0,0,0.15)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                transition: 'transform 0.2s ease, box-shadow 0.2s ease',
            }}
            onMouseEnter={(e) => {
                (e.currentTarget as HTMLButtonElement).style.transform = 'scale(1.1)';
                (e.currentTarget as HTMLButtonElement).style.boxShadow =
                '0 10px 28px rgba(0,0,0,0.22)';
            }}
            onMouseLeave={(e) => {
                (e.currentTarget as HTMLButtonElement).style.transform = 'scale(1)';
                (e.currentTarget as HTMLButtonElement).style.boxShadow =
                '0 6px 18px rgba(0,0,0,0.15)';
            }}
            >
            <BsStars size={34} color={'white'} />
        </button>
        </>
    );
}

const AiPrompt = ({ closeModal }: { closeModal: () => void; }) => {
    const [agentResponse, setAgentResponse] = useState('');
    const [isDisabledInput, setIsDisabledInput] = useState(false);
    const [isRecording, setIsRecording] = useState(false);
    const [showInstructions, setShowInstructions] = useState(false);
    const [text, setText] = useState('');
    const [usingBackendForTranscription, setUsingBackendForTranscriptions] = useState(false);
    const chunksRef = useRef<Blob[]>([]);
    const dispatch = useDispatch();
    const isAppleDevice = useIsAppleDevice();
    const mediaRecorderRef = useRef<MediaRecorder | null>(null);

    const handleVoiceStart = () => {
        const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
        const recognition = new SpeechRecognition();
        recognition.lang = 'es-AR';
        recognition.continuous = false;
        recognition.interimResults = false;
        setIsRecording(true);
        recognition.onresult = (event: any) => {
            const result = event.results[0][0].transcript;
            setText(prev => prev + (prev ? ' ' : '') + result);
        };
        recognition.onend = () => {
            setIsRecording(false);
        };
        recognition.start();
        (window as any).__activeRecognition = recognition;
    };

    const handleVoiceStop = () => {
        const instance = (window as any).__activeRecognition;
        if (instance && typeof instance.stop === 'function') {
            instance.stop();
        }
    }

    const sendRequest = async () => {
        if (text.length < 5) {
            return;
        }
        dispatch(showLoadingModalReducer());
        const response = await aiAgentServices.ExecuteCommands(text);
        dispatch(hideLoadingModalReducer());
        if (!response.success || !response.data?.aiAgentResponse) {
            dispatch(setAlertResponse({ response }));
            return;
        }
        setAgentResponse(`${response.data.aiAgentResponse}`);
        setIsDisabledInput(true);
    }

    const backendStartRecording = async () => {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        const mediaRecorder = new MediaRecorder(stream, {
            mimeType: 'audio/webm;codecs=opus',
        });
        chunksRef.current = [];
        setIsRecording(true);
        mediaRecorder.ondataavailable = e => {
            if (e.data.size > 0) {
                chunksRef.current.push(e.data);
            }
        };
        mediaRecorder.onstop = backendSendAudio;
        mediaRecorder.start();
        mediaRecorderRef.current = mediaRecorder;
    }

    const backendStopRecording = () => {
        setIsRecording(false);
        mediaRecorderRef.current?.stop();
    }

    const backendSendAudio = async () => {
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        const audioBase64 = await helpers.BlobToBase64(blob);
        if (!audioBase64.length) {
            return;
        }
        dispatch(showLoadingModalReducer());
        const response = await transcriptionServices.TranscribeFromBase64(audioBase64);
        dispatch(hideLoadingModalReducer());
        if (!response.success || !response.data?.transcription) {
            dispatch(setAlertResponse({ response }));
            return;
        }
        setText(`${response.data.transcription}`);
        setIsDisabledInput(false);
    }

    useEffect(() => {
        const speechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
        setUsingBackendForTranscriptions(isAppleDevice || !Boolean(speechRecognition));
    }, [isAppleDevice]);

    return (
        <Modal show
            fullscreen={'md-down'}
            onHide={closeModal}
            size={'lg'}
        >
            <Modal.Header className={'bg-dark text-white'} closeButton>
                <Modal.Title>
                    Agente de Inteligencia Artificial
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className={'bg-dark text-white d-flex flex-column h-100'}>
                {!agentResponse.length &&
                    <>
                    <p>
                        <span className={showInstructions ? 'd-none' : ''}>Por favor, dar instrucciones correctas y precisas y revisar los datos que se van a enviar para evitar errores, sobre todo si se cargaron por reconocimiento de voz.</span>
                        <span className={'c-pointer text-decoration-underline ms-1 mb-3'}
                            style={{ color: generalBlue, fontSize: '1rem' }}
                            onClick={() => setShowInstructions(x => !x)}
                        >
                            {showInstructions ? 'Volver al agente' : 'Ver cómo usar este agente'}
                        </span>
                    </p>
                    </>
                }
                {showInstructions ?
                    <div className={'ms-4'}>
                        <p>El agente puede realizar las siguientes tareas, individualmente o como plan orquestado:</p>
                        <ul>
                            <li>Consultar usuarios</li>
                            <li>Consultar asignaciones de reuniones</li>
                            <li>Enviar emails</li>
                        </ul>
                        <p>Al finalizar, entregará una respuesta indicando qué hizo.</p>
                    </div>
                    :
                    <>
                    <div className={'flex-grow-1 d-flex flex-column mb-4'}>
                        <textarea
                            className={'form-control flex-grow-1'}
                            style={{ resize: 'none', overflow: 'auto' }}
                            rows={7}
                            value={agentResponse || text}
                            onChange={e => setText(e.target.value)}
                            disabled={isDisabledInput}
                        />
                    </div>
                    {!agentResponse.length &&
                        <VoiceButton
                            handleVoiceStart={usingBackendForTranscription ? backendStartRecording : handleVoiceStart}
                            handleVoiceStop={usingBackendForTranscription ? backendStopRecording : handleVoiceStop}
                            recording={isRecording}
                        />
                    }
                    <Hr />
                    <div className={'d-flex justify-content-center gap-2 my-2'}>
                        <button className={'btn btn-grey w-25'}
                            onClick={() => {
                                setText('');
                                setAgentResponse('');
                                setIsDisabledInput(false);
                            }}
                        >
                            Limpiar
                        </button>
                        <button className={'btn btn-general-red w-25'} onClick={closeModal}>
                            Cerrar
                        </button>
                        {!agentResponse.length &&
                            <button className={'btn btn-general-blue w-25'} onClick={sendRequest}>
                                Aceptar
                            </button>
                        }
                    </div>
                    </>
                }
            </Modal.Body>
        </Modal>
    );
}

type propsType = {
    handleVoiceStart: () => void;
    handleVoiceStop: () => void;
    recording: boolean;
};

const VoiceButton = ({ handleVoiceStart, handleVoiceStop, recording }: propsType) => {
    return (
        <div className={'d-flex align-items-center justify-content-center'}>
            <style>
                {`
                    @keyframes pulseAnim {
                        0% {
                            transform: scale(0.6);
                            opacity: 0.7;
                        }
                        70% {
                            transform: scale(1);
                            opacity: 0;
                        }
                        100% {
                            transform: scale(0.6);
                            opacity: 0;
                        }
                    }
                    .no-ios-select, .no-ios-select * {
                        -webkit-user-select: none;
                        user-select: none;
                        -webkit-touch-callout: none;
                    }
                `}
            </style>
            <div className={`
                    d-flex align-items-center justify-content-center
                    border border-white rounded rounded-4 c-pointer
                    no-ios-select
                    py-2 px-4
                `}
                style={{ touchAction: 'none', userSelect: 'none' }}
                role={'button'}
                aria-pressed={recording}
                tabIndex={0}
                onPointerDown={handleVoiceStart}
                onPointerUp={handleVoiceStop}
                onPointerCancel={handleVoiceStop}
                onKeyDown={e => { if (e.key === ' ' || e.key === 'Enter') handleVoiceStart(); }}
                onKeyUp={e => { if (e.key === ' ' || e.key === 'Enter') handleVoiceStop(); }}
            >
                <span className={'me-3'} style={{ pointerEvents: 'none' }}>
                    Mantener para dictar
                </span>
                <div
                    className={`btn d-flex align-items-center justify-content-center position-relative
                        ${recording ? 'btn-danger' : 'btn-secondary'}
                    `}
                    style={{
                        borderRadius: '50%',
                        cursor: 'pointer',
                        height: 48,
                        padding: 0,
                        transition: 'transform 0.15s ease',
                        width: 48
                    }}
                >
                    {recording ? <LuMicOff size={22} /> : <LuMic size={22} />}
                    {recording &&
                        <span
                            className='position-absolute'
                            style={{
                                animation: 'pulseAnim 1.5s infinite',
                                border: '2px solid rgba(255,0,0,0.5)',
                                borderRadius: '50%',
                                height: '70px',
                                pointerEvents: 'none',
                                width: '70px'
                            }}
                        />
                    }
                </div>
            </div>
        </div>
    );
}
